import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type FormDialogSize = "sm" | "md" | "lg";

const sizeClass: Record<FormDialogSize, string> = {
  sm: "max-w-md",
  md: "max-w-xl",
  lg: "max-w-3xl",
};

interface FormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Small muted line under the title (e.g. an ID or hint). */
  subtitle?: React.ReactNode;
  size?: FormDialogSize;
  /** Sticky footer content (typically Anuluj + submit buttons). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared shell for create/edit modals — sticky brand header, scrollable body
 * on the grouped background, and a sticky footer. Wrap body content in
 * <FormSection> cards for the sectioned look.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  subtitle,
  size = "md",
  footer,
  children,
}: FormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[92vh] flex-col gap-0 overflow-hidden bg-pulso-bg p-0",
          sizeClass[size],
        )}
      >
        <DialogHeader className="border-b border-black/5 bg-pulso-surface px-6 py-4 text-left">
          <DialogTitle className="font-brand text-xl">{title}</DialogTitle>
          {subtitle && <p className="text-xs text-pulso-muted">{subtitle}</p>}
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-black/5 bg-pulso-surface px-6 py-4">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
